import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaArrowRight, 
  FaSearch, 
  FaCheck, 
  FaFacebookF, 
  FaGoogle, 
  FaInstagram,
  FaTwitter,
  FaLinkedinIn,
  FaChartLine,
  FaBullhorn,
  FaEnvelope,
  FaPencilAlt,
  FaShieldAlt,
  FaClock,
  FaUsers,
  FaStar,
  FaQuoteLeft,
  FaBullseye,
  FaRocket,
  FaChartBar,
  FaYoutube,
  FaPinterest
} from 'react-icons/fa';
import { SiGoogleads, SiGoogleanalytics, SiTiktok, SiSnapchat, SiHootsuite } from 'react-icons/si';
import { serviceSeoData, seoImage } from '../../config/pageSeoConfig';
import './DigitalMarketing.scss';

const DigitalMarketing: React.FC = () => {
  // SEO constants from config
  const seoTitle = serviceSeoData.digitalMarketing.title;
  const seoDescription = serviceSeoData.digitalMarketing.description;
  const seoKeywords = serviceSeoData.digitalMarketing.keywords;
  const seoUrl = serviceSeoData.digitalMarketing.url;
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Digital Marketing Services",
    "description": seoDescription,
    "provider": {
      "@type": "Organization",
      "name": "App Developer Pro",
      "url": "https://appdeveloperpro.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://appdeveloperpro.com/images/team-at-work-app-developer-pro.jpg"
      }
    },
    "areaServed": "Worldwide",
    "serviceType": "Digital Marketing, SEO, PPC, Google Ads, Social Media Marketing, Content Marketing, Analytics, Email Marketing, 2025",
    "url": seoUrl,
    "image": seoImage
  };
  const features = [
    {
      icon: FaSearch,
      title: 'Search Engine Optimization',
      description: 'Improve your Google rankings and organic traffic with proven SEO strategies'
    },
    {
      icon: FaGoogle,
      title: 'Google Ads Management',
      description: 'Strategic PPC campaigns that maximize ROI and drive qualified leads'
    },
    {
      icon: FaFacebookF,
      title: 'Social Media Marketing',
      description: 'Engaging social media campaigns across all major platforms'
    },
    {
      icon: FaPencilAlt,
      title: 'Content Marketing',
      description: 'High-quality content that educates, engages, and converts your audience'
    },
    {
      icon: FaEnvelope,
      title: 'Email Marketing',
      description: 'Automated email sequences that nurture leads and drive sales'
    },
    {
      icon: SiGoogleanalytics,
      title: 'Analytics & Reporting',
      description: 'Data-driven insights to optimize your marketing performance'
    }
  ];

  const platforms = [
    { icon: FaGoogle, name: 'Google Ads', color: '#4285F4' },
    { icon: FaFacebookF, name: 'Facebook', color: '#1877F2' },
    { icon: FaInstagram, name: 'Instagram', color: '#E4405F' },
    { icon: FaTwitter, name: 'Twitter', color: '#1DA1F2' },
    { icon: FaLinkedinIn, name: 'LinkedIn', color: '#0077B5' },
    { icon: SiGoogleads, name: 'Google Ads', color: '#4285F4' },
    { icon: SiGoogleanalytics, name: 'Analytics', color: '#FF6C00' },
    { icon: FaChartLine, name: 'SEMrush', color: '#FF642D' },
    { icon: FaEnvelope, name: 'Mailchimp', color: '#FFE01B' },
    { icon: FaBullhorn, name: 'HubSpot', color: '#FF7A59' },
    { icon: FaYoutube, name: 'YouTube', color: '#FF0000' },
    { icon: SiTiktok, name: 'TikTok', color: '#000000' },
    { icon: SiSnapchat, name: 'Snapchat', color: '#FFFC00' },
    { icon: FaPinterest, name: 'Pinterest', color: '#BD081C' },
    { icon: FaSearch, name: 'Bing Ads', color: '#00BCF2' },
    { icon: SiHootsuite, name: 'Hootsuite', color: '#1B3C5A' }
  ];

  const benefits = [
    {
      icon: FaBullseye,
      title: 'Targeted Campaigns',
      description: 'Precise audience targeting to reach your ideal customers effectively'
    },
    {
      icon: FaChartBar,
      title: 'Measurable Results',
      description: 'Transparent reporting with clear ROI metrics and performance tracking'
    },
    {
      icon: FaRocket,
      title: 'Growth Focused',
      description: 'Strategies designed to scale your business and increase revenue'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Strategy Development',
      description: 'Analyze your market, competitors, and audience to create a winning strategy'
    },
    {
      step: '02',
      title: 'Campaign Setup',
      description: 'Set up optimized campaigns across relevant digital channels'
    },
    {
      step: '03',
      title: 'Content Creation',
      description: 'Develop compelling content that resonates with your target audience'
    },
    {
      step: '04',
      title: 'Monitor & Optimize',
      description: 'Continuous monitoring and optimization for maximum performance'
    }
  ];

  const testimonials = [
    {
      text: "Our online visibility increased by 400% and lead generation improved dramatically. The ROI on digital marketing has been exceptional.",
      author: "Jennifer Adams",
      company: "Local Business Owner",
      rating: 5
    },
    {
      text: "The team's expertise in Google Ads and SEO helped us dominate our market. Our website traffic and sales have never been higher.",
      author: "Robert Taylor",
      company: "E-commerce Store",
      rating: 5
    }
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content="digital marketing, seo, ppc, google ads, social media marketing, content marketing, analytics, email marketing, online marketing, 2025" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoUrl} />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content="App Developer Pro" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <meta name="twitter:site" content="@appdeveloperpro" />
        {/* Schema.org Markup */}
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <div className="digital-marketing">
      {/* Hero Section */}
      <section className="digital-marketing__hero">
        <div className="digital-marketing__hero-content">
          <motion.div
            className="digital-marketing__hero-text"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="digital-marketing__hero-title">
              Strategic <span className="digital-marketing__hero-highlight">Digital Marketing</span> Services
            </h1>
            <p className="digital-marketing__hero-subtitle">
              Drive growth with data-driven digital marketing strategies that deliver real results. From comprehensive SEO campaigns and high-converting PPC ads to engaging social media marketing and content creation, we help businesses of all sizes reach their target audience and achieve measurable success in today's competitive digital landscape.
            </p>
            <div className="digital-marketing__hero-features">
              <div className="digital-marketing__hero-feature">
                <FaCheck className="digital-marketing__hero-feature-icon" />
                <span>500+ Successful Campaigns Launched</span>
              </div>
              <div className="digital-marketing__hero-feature">
                <FaCheck className="digital-marketing__hero-feature-icon" />
                <span>Average 300% ROI Increase</span>
              </div>
              <div className="digital-marketing__hero-feature">
                <FaCheck className="digital-marketing__hero-feature-icon" />
                <span>Multi-Platform Expertise</span>
              </div>
            </div>
            <div className="digital-marketing__hero-stats">
              <div className="digital-marketing__hero-stat">
                <div className="digital-marketing__hero-stat-number">150+</div>
                <div className="digital-marketing__hero-stat-label">Happy Clients</div>
              </div>
              <div className="digital-marketing__hero-stat">
                <div className="digital-marketing__hero-stat-number">5M+</div>
                <div className="digital-marketing__hero-stat-label">Leads Generated</div>
              </div>
              <div className="digital-marketing__hero-stat">
                <div className="digital-marketing__hero-stat-number">24/7</div>
                <div className="digital-marketing__hero-stat-label">Support</div>
              </div>
            </div>
            <div className="digital-marketing__hero-buttons">
              <Link to="/contact" className="digital-marketing__hero-btn digital-marketing__hero-btn--primary">
                Get Free Audit <FaArrowRight />
              </Link>
              <Link to="/portfolio" className="digital-marketing__hero-btn digital-marketing__hero-btn--secondary">
                View Case Studies
              </Link>
            </div>
          </motion.div>
          <motion.div
            className="digital-marketing__hero-visual"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="digital-marketing__hero-dashboard">
              <div className="digital-marketing__hero-dashboard-header">
                <div className="digital-marketing__hero-dashboard-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span>Marketing Dashboard</span>
              </div>
              <div className="digital-marketing__hero-dashboard-content">
                <div className="digital-marketing__hero-dashboard-metrics">
                  <motion.div 
                    className="digital-marketing__hero-dashboard-metric"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1, duration: 0.6 }}
                  >
                    <div className="digital-marketing__hero-dashboard-metric-icon">
                      <FaUsers />
                    </div>
                    <motion.div 
                      className="digital-marketing__hero-dashboard-metric-value"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.2, duration: 0.8 }}
                    >
                      125K
                    </motion.div>
                    <div className="digital-marketing__hero-dashboard-metric-label">Reach</div>
                    <div className="digital-marketing__hero-dashboard-metric-change">+24%</div>
                  </motion.div>
                  <motion.div 
                    className="digital-marketing__hero-dashboard-metric"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.1, duration: 0.6 }}
                  >
                    <div className="digital-marketing__hero-dashboard-metric-icon">
                      <FaChartLine />
                    </div>
                    <motion.div 
                      className="digital-marketing__hero-dashboard-metric-value"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.3, duration: 0.8 }}
                    >
                      8.5%
                    </motion.div>
                    <div className="digital-marketing__hero-dashboard-metric-label">CTR</div>
                    <div className="digital-marketing__hero-dashboard-metric-change">+15%</div>
                  </motion.div>
                  <motion.div 
                    className="digital-marketing__hero-dashboard-metric"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.2, duration: 0.6 }}
                  >
                    <div className="digital-marketing__hero-dashboard-metric-icon">
                      <FaRocket />
                    </div>
                    <motion.div 
                      className="digital-marketing__hero-dashboard-metric-value"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.4, duration: 0.8 }}
                    >
                      342%
                    </motion.div>
                    <div className="digital-marketing__hero-dashboard-metric-label">ROI</div>
                    <div className="digital-marketing__hero-dashboard-metric-change">+89%</div>
                  </motion.div>
                  <motion.div 
                    className="digital-marketing__hero-dashboard-metric"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.3, duration: 0.6 }}
                  >
                    <div className="digital-marketing__hero-dashboard-metric-icon">
                      <FaBullseye />
                    </div>
                    <motion.div 
                      className="digital-marketing__hero-dashboard-metric-value"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.5, duration: 0.8 }}
                    >
                      92%
                    </motion.div>
                    <div className="digital-marketing__hero-dashboard-metric-label">Quality</div>
                    <div className="digital-marketing__hero-dashboard-metric-change">+8%</div>
                  </motion.div>
                </div>
                <div className="digital-marketing__hero-dashboard-chart">
                  <div className="digital-marketing__hero-dashboard-chart-header">
                    <span>Campaign Performance</span>
                    <span>Last 30 days</span>
                  </div>
                  <div className="digital-marketing__hero-dashboard-chart-bars">
                    <motion.div 
                      className="digital-marketing__hero-dashboard-chart-bar"
                      initial={{ height: 0 }}
                      animate={{ height: "60%" }}
                      transition={{ delay: 2, duration: 0.8, ease: "easeOut" }}
                    />
                    <motion.div 
                      className="digital-marketing__hero-dashboard-chart-bar"
                      initial={{ height: 0 }}
                      animate={{ height: "80%" }}
                      transition={{ delay: 2.2, duration: 0.8, ease: "easeOut" }}
                    />
                    <motion.div 
                      className="digital-marketing__hero-dashboard-chart-bar"
                      initial={{ height: 0 }}
                      animate={{ height: "40%" }}
                      transition={{ delay: 2.4, duration: 0.8, ease: "easeOut" }}
                    />
                    <motion.div 
                      className="digital-marketing__hero-dashboard-chart-bar"
                      initial={{ height: 0 }}
                      animate={{ height: "90%" }}
                      transition={{ delay: 2.6, duration: 0.8, ease: "easeOut" }}
                    />
                    <motion.div 
                      className="digital-marketing__hero-dashboard-chart-bar"
                      initial={{ height: 0 }}
                      animate={{ height: "70%" }}
                      transition={{ delay: 2.8, duration: 0.8, ease: "easeOut" }}
                    />
                    <motion.div 
                      className="digital-marketing__hero-dashboard-chart-bar"
                      initial={{ height: 0 }}
                      animate={{ height: "95%" }}
                      transition={{ delay: 3, duration: 0.8, ease: "easeOut" }}
                    />
                    <motion.div 
                      className="digital-marketing__hero-dashboard-chart-bar"
                      initial={{ height: 0 }}
                      animate={{ height: "85%" }}
                      transition={{ delay: 3.2, duration: 0.8, ease: "easeOut" }}
                    />
                  </div>
                </div>
                <div className="digital-marketing__hero-dashboard-social">
                  <motion.div 
                    className="digital-marketing__hero-dashboard-platform"
                    animate={{ 
                      scale: [1, 1.1, 1],
                      boxShadow: [
                        "0 4px 15px rgba(66, 133, 244, 0.3)",
                        "0 8px 25px rgba(66, 133, 244, 0.5)",
                        "0 4px 15px rgba(66, 133, 244, 0.3)"
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0 }}
                  >
                    <FaGoogle />
                  </motion.div>
                  <motion.div 
                    className="digital-marketing__hero-dashboard-platform"
                    animate={{ 
                      scale: [1, 1.1, 1],
                      boxShadow: [
                        "0 4px 15px rgba(24, 119, 242, 0.3)",
                        "0 8px 25px rgba(24, 119, 242, 0.5)",
                        "0 4px 15px rgba(24, 119, 242, 0.3)"
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                  >
                    <FaFacebookF />
                  </motion.div>
                  <motion.div 
                    className="digital-marketing__hero-dashboard-platform"
                    animate={{ 
                      scale: [1, 1.1, 1],
                      boxShadow: [
                        "0 4px 15px rgba(228, 64, 95, 0.3)",
                        "0 8px 25px rgba(228, 64, 95, 0.5)",
                        "0 4px 15px rgba(228, 64, 95, 0.3)"
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                  >
                    <FaInstagram />
                  </motion.div>
                  <motion.div 
                    className="digital-marketing__hero-dashboard-platform"
                    animate={{ 
                      scale: [1, 1.1, 1],
                      boxShadow: [
                        "0 4px 15px rgba(29, 161, 242, 0.3)",
                        "0 8px 25px rgba(29, 161, 242, 0.5)",
                        "0 4px 15px rgba(29, 161, 242, 0.3)"
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity, delay: 1.5 }}
                  >
                    <FaTwitter />
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="digital-marketing__features">
        <div className="digital-marketing__section-header">
          <motion.h2
            className="digital-marketing__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Our Digital Marketing Services
          </motion.h2>
          <motion.p
            className="digital-marketing__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Comprehensive digital marketing solutions to grow your business online
          </motion.p>
        </div>
        <div className="digital-marketing__features-grid">
          {features.map((feature, index) => {
            const FeatureIcon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                className="digital-marketing__feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="digital-marketing__feature-header">
                  <div className="digital-marketing__feature-icon">
                    <FeatureIcon />
                  </div>
                  <h3 className="digital-marketing__feature-title">{feature.title}</h3>
                </div>
                <p className="digital-marketing__feature-description">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Platforms Section */}
      <section className="digital-marketing__platforms">
        <motion.div
          className="digital-marketing__section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="digital-marketing__section-title">Platforms We Work With</h2>
          <p className="digital-marketing__section-subtitle">
            Expert management across all major digital marketing platforms
          </p>
        </motion.div>
        <div className="digital-marketing__platforms-grid">
          {platforms.map((platform, index) => {
            const PlatformIcon = platform.icon;
            return (
              <motion.div
                key={platform.name}
                className="digital-marketing__platform-item"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.1, y: -5 }}
              >
                <div 
                  className="digital-marketing__platform-icon"
                  style={{ '--platform-color': platform.color } as React.CSSProperties}
                >
                  <PlatformIcon />
                </div>
                <span className="digital-marketing__platform-name">{platform.name}</span>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="digital-marketing__benefits">
        <div className="digital-marketing__section-header">
          <motion.h2
            className="digital-marketing__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Why Choose Our Digital Marketing Services
          </motion.h2>
        </div>
        <div className="digital-marketing__benefits-grid">
          {benefits.map((benefit, index) => {
            const BenefitIcon = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                className="digital-marketing__benefit-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="digital-marketing__benefit-icon">
                  <BenefitIcon />
                </div>
                <h3 className="digital-marketing__benefit-title">{benefit.title}</h3>
                <p className="digital-marketing__benefit-description">{benefit.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Process Section */}
      <section className="digital-marketing__process">
        <div className="digital-marketing__section-header">
          <motion.h2
            className="digital-marketing__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Our Marketing Process
          </motion.h2>
          <motion.p
            className="digital-marketing__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            A data-driven approach to digital marketing success
          </motion.p>
        </div>
        <div className="digital-marketing__process-timeline">
          {process.map((step, index) => (
            <motion.div
              key={step.step}
              className="digital-marketing__process-step"
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <div className="digital-marketing__process-number">{step.step}</div>
              <div className="digital-marketing__process-content">
                <h3 className="digital-marketing__process-title">{step.title}</h3>
                <p className="digital-marketing__process-description">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="digital-marketing__testimonials">
        <div className="digital-marketing__section-header">
          <motion.h2
            className="digital-marketing__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Client Success Stories
          </motion.h2>
        </div>
        <div className="digital-marketing__testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.author}
              className="digital-marketing__testimonial-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="digital-marketing__testimonial-quote">
                <FaQuoteLeft />
              </div>
              <div className="digital-marketing__testimonial-stars">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>
              <p className="digital-marketing__testimonial-text">"{testimonial.text}"</p>
              <div className="digital-marketing__testimonial-author">
                <strong>{testimonial.author}</strong>
                <span>{testimonial.company}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="digital-marketing__cta">
        <motion.div
          className="digital-marketing__cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="digital-marketing__cta-title">Ready to Grow Your Business Online?</h2>
          <p className="digital-marketing__cta-text">
            Get a free digital marketing audit and discover opportunities to increase your online presence and drive more qualified leads.
          </p>
          <div className="digital-marketing__cta-buttons">
            <Link to="/contact" className="digital-marketing__cta-btn digital-marketing__cta-btn--primary">
              Get Free Audit <FaArrowRight />
            </Link>
            <Link to="/portfolio" className="digital-marketing__cta-btn digital-marketing__cta-btn--secondary">
              View Case Studies
            </Link>
          </div>
        </motion.div>
      </section>

      {/* FAQ Section - SEO Boost */}
      <section className="digital-marketing__faq">
        <div className="container">
          <motion.div
            className="digital-marketing__section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Frequently Asked Questions About Digital Marketing</h2>
            <p>Everything you need to know about growing your business online</p>
          </motion.div>
          <div className="digital-marketing__faq-list">
            <motion.div
              className="digital-marketing__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>How much should I budget for digital marketing?</h3>
              <p>Digital marketing budgets vary by business size and goals. Small businesses typically invest $1,000-$5,000/month, mid-size companies spend $5,000-$20,000/month, and enterprises allocate $20,000-$100,000+/month. We recommend allocating 7-12% of revenue to marketing for optimal growth and competitive positioning.</p>
            </motion.div>
            <motion.div
              className="digital-marketing__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3>How long does it take to see results from SEO?</h3>
              <p>SEO is a long-term strategy. You'll typically see initial improvements in 3-4 months, significant results in 6-9 months, and substantial traffic growth after 12 months. However, paid advertising (PPC) can drive immediate traffic. We recommend a balanced approach combining quick wins with sustainable long-term growth.</p>
            </motion.div>
            <motion.div
              className="digital-marketing__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>What is the ROI of digital marketing?</h3>
              <p>ROI varies by industry and strategy. Email marketing averages 4200% ROI ($42 return per $1 spent), SEO provides 22:1 ROI on average, PPC typically returns $2-$8 per $1 spent, and social media advertising ranges from 3-5x ROI. We track all metrics and continuously optimize campaigns to maximize your return.</p>
            </motion.div>
            <motion.div
              className="digital-marketing__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3>Do I need SEO and PPC, or just one?</h3>
              <p>Both work better together! SEO provides sustainable long-term traffic and credibility, while PPC delivers immediate visibility and targeted leads. SEO builds authority over time, PPC tests keywords quickly. The combination maximizes visibility, dominates search results, and provides short-term wins while building long-term assets.</p>
            </motion.div>
            <motion.div
              className="digital-marketing__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3>How do you measure digital marketing success?</h3>
              <p>We track comprehensive metrics including website traffic, conversion rates, cost per acquisition (CPA), return on ad spend (ROAS), keyword rankings, engagement rates, lead quality, customer lifetime value (CLV), and revenue attribution. You receive detailed monthly reports with insights and recommendations for improvement.</p>
            </motion.div>
            <motion.div
              className="digital-marketing__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>Can you manage my social media accounts?</h3>
              <p>Yes! We offer complete social media management including content creation, posting schedules, community engagement, paid advertising, influencer partnerships, and analytics reporting. We manage all major platforms: Facebook, Instagram, LinkedIn, Twitter, TikTok, and YouTube to build your brand and engage your audience.</p>
            </motion.div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default DigitalMarketing;
